import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import MultiplicationActivityWrapper from '~/bootcamp/Activities/Multiplication/components/MultiplicationActivityWrapper'
import Times from '~/bootcamp/Activities/components/Times'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

import { TermRange } from './types'
import { getRoundMultiTerms } from './util'

export default function RoundMulti({
  answerState,
  onAnswerStateChange,
  onInstanceChange,
  difficultyLevel,
  onNextQuestionClick,
  actionButtonsFC,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const [answer, setAnswer] = useState('')
  const [inputKey, setInputKey] = useState<string>(uuid())
  const [taskHistory, setTaskHistory] = useState<Array<TermRange<number>>>([])
  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)
  const { t } = useTranslation('generics')

  const { multiplicand, multiplier } = useMemo(() => {
    if (!difficultyLevel) {
      return { multiplicand: 0, multiplier: 0 }
    }
    const numbersHistory = taskHistory.map((item) => item.multiplier)
    return getRoundMultiTerms(difficultyLevel, numbersHistory)
  }, [difficultyLevel, taskHistory])

  useEffect(() => {
    setAnswer('')
  }, [multiplicand, multiplier])

  useEmitInstance(
    onInstanceChange,
    multiplier && multiplicand ? `${multiplier} \\cdot ${multiplicand} = \\square` : null,
  )

  const handleOnCheckAnswerClick = (): void => {
    const correctAnswer = multiplier * multiplicand
    onAnswerStateChange(correctAnswer === parseFloat(answer))
  }

  const handleOnNextQuestionClick = (): void => {
    onAnswerStateChange(null)
    setAnswer('')

    setTaskHistory((prevState) => {
      return [{ multiplicand, multiplier }, ...prevState].slice(0, 8)
    })

    onNextQuestionClick()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (answer !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper className="py-5">
      <MultiplicationActivityWrapper>
        <div>
          <span>{multiplier}</span>
          <Times />
          <span>{multiplicand}</span>
        </div>
        <div>
          <Equals />
          <Input
            aria-label={t('ariaLabelButtons.insertProduct')}
            autoFocus={autofocus}
            isError={answerState === false}
            isWide
            key={inputKey}
            maxDigits={15}
            onChange={(value) => {
              setAnswer(value)
              onAnswerStateChange(null)
            }}
            onEnter={onEnterPress}
            ref={inputRef}
            value={answer}
          />
        </div>
      </MultiplicationActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== '',
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => setAnswer(''),
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}
